import React from 'react'
import { Box, Button, Grid, Heading } from '@chakra-ui/react'
import { ArrowRight } from '@emotion-icons/feather'
import { ResultOf } from '@graphql-typed-document-node/core'
import { gql } from '@urql/core'
import { Company } from 'entities/company'
import { Pagination } from 'entities/pagination'
import { routes } from 'routes'
import { isServerSide } from 'lib/env'
import useQuery from 'hooks/useQuery'
import {
    BlogCard,
    BlogCardData,
    FRAGMENT_BLOG_CARD,
} from 'containers/blogs/components/BlogCard'

export const QUERY_COMPANY = gql<
    {
        company: Pick<Company, 'id' | 'name' | 'slug'> & {
            blogs: { blogs: BlogCardData[]; pagination: Pick<Pagination, 'total'> }
        }
    },
    { id?: string; pageSize: number }
>`
    query company($id: ID, $pageSize: Int) {
        company(id: $id) {
            id
            name
            slug
            blogs(pageNumber: 1, pageSize: $pageSize) {
                blogs {
                    ...BlogCard
                }
                pagination {
                    total
                }
            }
        }
    }
    ${FRAGMENT_BLOG_CARD}
`

const DEFAULT_NUM_OF_BLOGS_TO_DISPLAY = 1

type RelatedBlogsInnerProps = (
    | ResultOf<typeof QUERY_COMPANY>
    | { company: undefined }
) & { numBlogs?: number }

export const RelatedBlogsInner = ({
    company,
    numBlogs = DEFAULT_NUM_OF_BLOGS_TO_DISPLAY,
}: RelatedBlogsInnerProps) => {
    const blogs = company?.blogs.blogs
    const blogCount = company?.blogs.pagination.total ?? 0

    if (!blogs || blogs.length === 0 || !company) {
        return null
    }

    // NB: These `indices` and empty boxes are necessary for a single grid item
    //     to not stretch over the full available space.
    const indices = Array.from(Array(numBlogs).keys())

    return (
        <Box>
            <Heading size="sm" mb={{ base: '16px', md: '24px' }} noOfLines={1}>
                Articles about {company.name}
            </Heading>
            <Grid
                templateColumns={{
                    base: '100%',
                    sm: `repeat(auto-fit, minmax(320px, 1fr))`,
                }}
                gridGap={{ base: '16px', sm: '24px' }}
            >
                {indices.map(idx => {
                    const blog = blogs && blogs[idx]
                    return (
                        <Box key={blog?.id ?? idx}>
                            {blog && <BlogCard blog={blog} />}
                        </Box>
                    )
                })}
            </Grid>
            {blogCount > numBlogs && (
                <Button
                    variant={'secondary'}
                    size={'md'}
                    w={'100%'}
                    mt={{ base: '16px', md: '24px' }}
                    as={'a'}
                    href={routes.companyArticles({
                      companySlug: company.slug
                    })}
                    rightIcon={
                        <ArrowRight width={'18px'} height={'18px'} strokeWidth={'2px'} />
                    }
                >
                    All company articles ({blogCount})
                </Button>
            )}
        </Box>
    )
}

export const RelatedBlogs = ({
    companyId,
    numBlogs = DEFAULT_NUM_OF_BLOGS_TO_DISPLAY,
}: {
    companyId: Company['id']
    numBlogs?: number
}) => {
    const [result] = useQuery({
        query: QUERY_COMPANY,
        variables: {
            id: companyId,
            pageSize: numBlogs,
        },
        pause: isServerSide,
    })

    return <RelatedBlogsInner company={result.data?.company} numBlogs={numBlogs} />
}
